import { DebugService } from '../shared/services/debug.service.js';
import { REFI_VEHICLE_LOAN_CONST } from '../shared/constants/app.constants.js';
import { SessionService } from '../core/services/session/session.service.js';
import { ApiService } from '../core/services/api/api.service.js';
import { AppService } from '../app.service.js';
import { ApplicationService } from './application.service.js';
import { POLICY_LIMITS_CONST } from '../shared/services/policy-limits.service.js';

class _VehicleService {
  constructor() {
    this.debug = new DebugService('VehicleService');
    this._vehicleList = [];
    this._lookupType = REFI_VEHICLE_LOAN_CONST.VEHICLE_LOOKUP_TYPE.SSN;
    this._selectedVehicle = {};
    this._vehicleLookupModel = {
      residenceStateCode: '',
      licensePlateNumber: '',
      licensePlateStateCode: '',
      vin: '',
    };
    this.session = SessionService.session || {};
  }

  set selectedVehicle(veh) {
    this.debug.log('updating selected vehicle with ', veh);
    this._selectedVehicle = veh;
    ApplicationService.selectedVehicle = veh;
    this.saveVehiclePrequalInfo(veh);
  }

  get selectedVehicle() {
    return this._selectedVehicle;
  }

  get vehicleLookupModel() {
    return this._vehicleLookupModel;
  }

  set vehicleLookupModel(newModel) {
    this._vehicleLookupModel = newModel;
  }

  get vehicleList() {
    return this._vehicleList;
  }

  set vehicleList(vehicleList) {
    this._vehicleList = vehicleList;
  }

  get lookupType() {
    return this._lookupType;
  }

  setResidentState(newState) {
    this.debug.log('updating vehLookupModel states to', newState);
    if (!SessionService.getLicensePlateStateCode()) {
      this._vehicleLookupModel.licensePlateStateCode = newState;
    } else {
      this.debug.log('updating licensePlateStateCode to cached state');
      this._vehicleLookupModel.licensePlateStateCode =
        SessionService.getLicensePlateStateCode();
    }

    this._vehicleLookupModel.residenceStateCode = newState;
  }

  vehicleLookup(lookupObj, lookupType) {
    const lookupBody = {};
    this.debug.log(`vehicleLookup called with type ${lookupType}`, lookupObj);
    this._lookupType = lookupType;

    if (
      this.lookupType ===
      REFI_VEHICLE_LOAN_CONST.VEHICLE_LOOKUP_TYPE.LICENSE_PLATE
    ) {
      Object.assign(lookupBody, lookupObj);
      delete lookupBody.vin;
    } else {
      Object.assign(lookupBody, lookupObj);
      lookupBody.vin = lookupBody.vin.toUpperCase();
      delete lookupBody.licensePlateStateCode;
      delete lookupBody.licensePlateNumber;
    }

    this.customHeaders = {
      headers: REFI_VEHICLE_LOAN_CONST.HTTP_HEADERS,
      observe: 'response',
    };
    const url =
      REFI_VEHICLE_LOAN_CONST.VEHICLE_AND_LOAN_BFF_BASE_PATH +
      REFI_VEHICLE_LOAN_CONST.BFF_SEARCH_ENDPOINT_MAP[lookupType];

    return ApiService.post(url, lookupBody, this.customHeaders).then(
      (res) => {
        const vehList = res.vehicleList;
        if (vehList.length > 0) {
          this.vehicleList = this.validateVehicleList(vehList);
          this.vehicleList = this.sortVehicleList(vehList);
          return res;
        }
        throw new Error('no vehicles');
      },
      (err) => {
        throw new Error(`operation unsuccessful ${err}`);
      }
    );
  }

  getWarningText(ineligibilityReason, maxVehicleAge, position = null) {
    this.debug.log(position);
    switch (ineligibilityReason.toUpperCase()) {
      case 'MAKE':
        return `We're unable to refinance vehicles of this make.`;
      case 'YEAR':
        return `We're unable to refinance vehicles with a model year older than ${
          new Date().getFullYear() - maxVehicleAge
        }.`;
      case 'MODEL':
        return `We're unable to refinance vehicles of this model.`;
      case 'NO_WHOLESALE':
        return `We're unable to refinance vehicles without an established resale value.`;
      case 'MOTORCYCLE':
        return `We're unable to refinance motorcycles.`;
      default:
        return '';
    }
  }

  sendVehiclePrequalInfoToSDP(vehicle, saveToSession, errorMessage = '') {
    const sessionPrequalInfo = SessionService.getPrequalInfo() || {};
    const vehiclePrequalInfo = this.createVehiclePrequalInfo(
      vehicle,
      errorMessage
    );
    sessionPrequalInfo.vehicleDetails = vehiclePrequalInfo.vehicleDetails;
    AppService.saveVehicleLoanPrequalInfo(vehiclePrequalInfo);
    if (saveToSession) {
      SessionService.setPrequalInfo(sessionPrequalInfo);
    }
  }

  saveVehiclePrequalInfo(vehicleDetails) {
    this.sendVehiclePrequalInfoToSDP(vehicleDetails, true);
  }

  createVehiclePrequalInfo(vehicleDetails, errorMessage = undefined) {
    this.debug.log('createVehiclePrequalInfo called ', vehicleDetails);
    const body = {};
    body.vehicleDetails = {
      year: vehicleDetails.year,
      make: vehicleDetails.make || '',
      model: vehicleDetails.model || '',
      trim: vehicleDetails.trim || '',
      vin: vehicleDetails.vin,
      wholesaleValueAmount: vehicleDetails.wholeSaleValue,
      state: this.vehicleLookupModel.licensePlateStateCode,
      licensePlate: this.vehicleLookupModel.licensePlateNumber,
      errorCode: undefined,
      errorMessage,
    };
    return body;
  }

  inSsnSearchState(applicant) {
    return (
      REFI_VEHICLE_LOAN_CONST.SSN_VEHICLE_LOOKUP_STATES.indexOf(
        applicant.currentAddress.state
      ) !== -1
    );
  }

  resetVehService() {
    this.debug.log('resetting VehicleService');
    this._lookupType = REFI_VEHICLE_LOAN_CONST.VEHICLE_LOOKUP_TYPE.SSN;
    this.vehicleList = [];
    this._vehicleLookupModel = {
      ...this._vehicleLookupModel,
      licensePlateNumber: '',
      vin: '',
    };
  }

  isEligible(vehicle) {
    return vehicle.ineligibleReasons.length === 0;
  }

  sortVehicleList(vehicles) {
    this.debug.log('sortVehicleList called with', vehicles);
    this.vehicleList = vehicles.sort((a, b) => {
      let result = (this.isEligible(a) ? 0 : 1) - (this.isEligible(b) ? 0 : 1);
      if (result === 0) {
        result = parseInt(b.year, 10) - parseInt(a.year, 10);
      }
      return result;
    });
    return this.vehicleList;
  }

  get selectVehicleButtonCTA() {
    if (ApplicationService.isIconicV2) {
      return 'Select this car';
    }
    if (ApplicationService.isInAppDeclineDelayOn) {
      return this._vehicleList.length > 1
        ? 'Select this car'
        : 'Yes, looks good';
    }
    return 'Use this car';
  }

  validateVehicleList(vehicleList) {
    for (let vehicle of vehicleList) {
      if (vehicle) {
        vehicle.maxVehicleAge = POLICY_LIMITS_CONST.MAX_VEHICLE_AGE();
        vehicle = this.validateVehicle(vehicle);
      }
    }
    return vehicleList;
  }

  validateVehicle(vehicle) {
    const _vehicle = vehicle;
    const { year } = _vehicle;

    const _ineligibleReasons = _vehicle.ineligibleReasons
      ? _vehicle.ineligibleReasons
      : [];

    if (year) {
      const vehicleAge = new Date().getFullYear() - year;
      if (vehicleAge > POLICY_LIMITS_CONST.MAX_VEHICLE_AGE()) {
        _ineligibleReasons.push('YEAR');
      }
    }

    if (_ineligibleReasons.length > 0) {
      _vehicle.ineligibleReasons = _ineligibleReasons;
    }

    return _vehicle;
  }

  getMinVehicleYear() {
    return new Date().getFullYear() - POLICY_LIMITS_CONST.MAX_VEHICLE_AGE();
  }
}

export const VehicleService = new _VehicleService();
